import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_LANGUAGE, LanguageCode, toBackendCulture } from '../../core/i18n/languages';
import LanguageService from '../../core/services/LanguageService';
import UserLanguageService from '../../core/services/UserLanguageService';
import { formatCurrency, formatDate } from '../../core/i18n/formatters';
import { caseVerificationTranslations } from './caseVerificationTranslations';
import { CaseStatusTokenError } from '../../logic/components/CaseStatus/api/CaseStatusApi';

type CaseStatusLanguageContextValue = {
  language: LanguageCode;
  culture: string;
  setLanguage: (code: LanguageCode) => void;
  translationsReady: boolean;
  enableTranslations: () => void;
  t: (key: string, ...args: (string | number)[]) => string;
  fmtCurrency: (amount: number | string, currency: string) => string;
  fmtDate: (iso: string | null | undefined) => string;
};

const defaultLanguageContext: CaseStatusLanguageContextValue = {
  language: DEFAULT_LANGUAGE,
  culture: 'en-us',
  setLanguage: () => {},
  translationsReady: true,
  enableTranslations: () => {},
  t: (key) => key,
  fmtCurrency: (amount, currency) => `${amount} ${currency}`.trim(),
  fmtDate: (iso) => iso ?? '',
};

const CaseStatusLanguageContext = createContext<CaseStatusLanguageContextValue>(defaultLanguageContext);

export const useCaseStatusLanguage = (): CaseStatusLanguageContextValue =>
  useContext(CaseStatusLanguageContext);

type Props = {
  children: React.ReactNode;
  embedded?: boolean;
  deferLoad?: boolean;
  loadTranslations?: (culture: string) => Promise<Record<string, string>>;
  onTokenExpired?: () => void;
};

const CaseStatusLanguageProvider = ({
  children,
  embedded = false,
  deferLoad = false,
  loadTranslations,
  onTokenExpired,
}: Props) => {
  const [language, setLanguage] = useState<LanguageCode>(() => {
    const resolved = LanguageService.resolve();
    UserLanguageService.setLanguageCode(toBackendCulture(resolved) ?? resolved);
    return resolved;
  });

  const culture = toBackendCulture(language) ?? 'en-us';

  // Translations are ready up-front when there is nothing to fetch (embedded bundle or no loader).
  const staticallyReady = embedded || !loadTranslations;

  const [dict, setDict] = useState<Record<string, string>>(() =>
    embedded ? caseVerificationTranslations[language] ?? {} : {}
  );
  const [translationsReady, setTranslationsReady] = useState(staticallyReady);
  // Once translations have loaded once, later language switches swap in place without a spinner.
  const hasLoadedRef = useRef(staticallyReady);

  const [loadEnabled, setLoadEnabled] = useState(!deferLoad);
  const enableTranslations = useCallback(() => setLoadEnabled(true), []);

  useEffect(() => {
    if (embedded) {
      setDict(caseVerificationTranslations[language] ?? {});
      return;
    }

    if (!loadTranslations || !loadEnabled) {
      return;
    }

    let active = true;
    if (!hasLoadedRef.current) {
      setTranslationsReady(false);
    }
    loadTranslations(culture)
      .then((d) => {
        if (active) {
          setDict(d ?? {});
          setTranslationsReady(true);
          hasLoadedRef.current = true;
        }
      })
      .catch((error) => {
        if (active && error instanceof CaseStatusTokenError) {
          onTokenExpired?.();
        }
      });
    return () => {
      active = false;
    };
  }, [culture, language, embedded, loadEnabled, loadTranslations, onTokenExpired]);

  const changeLanguage = useCallback((code: LanguageCode) => {
    LanguageService.set(code);
    UserLanguageService.setLanguageCode(toBackendCulture(code) ?? code);
    setLanguage(code);
  }, []);

  const t = useCallback(
    (key: string, ...args: (string | number)[]) => {
      let text = dict[key] ?? key;
      args.forEach((arg, i) => {
        text = text.replace(`{${i}}`, String(arg));
      });
      return text;
    },
    [dict]
  );

  const value = useMemo<CaseStatusLanguageContextValue>(
    () => ({
      language,
      culture,
      setLanguage: changeLanguage,
      translationsReady,
      enableTranslations,
      t,
      fmtCurrency: (amount, currency) => formatCurrency(amount, currency, culture),
      fmtDate: (iso) => formatDate(iso, culture),
    }),
    [language, culture, changeLanguage, translationsReady, t]
  );
  return (
    <CaseStatusLanguageContext.Provider value={value}>{children}</CaseStatusLanguageContext.Provider>
  );
};

export default CaseStatusLanguageProvider;
